/**
 * What is LEFT of the old top toolbar.
 *
 * This class used to build the entire bar in one `_render()`. The command bar
 * has taken it apart control by control, and each departure was a whole slot
 * changing owner rather than a partial rewrite — see
 * `ui/command-bar/command-bar.js` for the slot manifest and the one-owner rule.
 *
 * Gone, and where to:
 *   · undo / redo, viewport controls  → row 2 anchors            (CMD-2)
 *   · page tabs + add-page            → `ui/command-bar/page-tabs.js`        (CMD-3)
 *   · sidebar toggle, project chip + menu, hidden file input
 *                                     → `ui/command-bar/identity-region.js`  (CMD-3)
 *   · telemetry chip (minus its session timer), presence mount
 *                                     → `ui/command-bar/save-pill.js`        (CMD-3)
 *   · agent, Share (new), Publish split (was Export), account chip
 *                                     → `ui/command-bar/destination-region.js` (CMD-3)
 *   · the preview toggle              → `ui/command-bar/preview-toggle.js`   (CMD-8)
 *   · the ⋮ menu — DELETED, not re-labelled. Its last four survivors got real
 *     surfaces: tour + shortcuts → `ui/command-bar/help-menu.js`, links → the
 *     page pane, code injection → the left rail's Project tab       (CMD-8)
 *
 * Still here, and why:
 *   · the ⌘K hint and its menu-hunt heuristic — deleted in CMD-9 with
 *     `pb-toolbar-pulse-once`. The heuristic is still fed by
 *     `toolbar:menu-opened`, which every command-bar menu emits, so it keeps
 *     working for the one release in which it still exists.
 *   · the undo/redo TOAST, which is not a toolbar control and never was
 *
 * There is nothing left in here that a user can click except the hint.
 */

import { eventBus } from '../core/events.js';
import { showToast } from './toast.js';

const CMDK_HINT_STORAGE_KEY = 'pb-cmdk-hint-dismissed';
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
const CMDK_HINT_LABEL = IS_MAC ? '⌘K · /' : 'Ctrl+K · /';

// The Cmd+K hint chip re-pulses after this many ms of dismissal so the
// shortcut becomes discoverable again rather than disappearing forever.
const CMDK_HINT_REPEAT_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

// Usage-aware re-show: a user who opens this many dropdown menus in one session
// without touching the palette is hunting for commands — surface the hint again
// even inside the snooze window (session-only; the stored snooze timestamp is
// untouched).
const CMDK_HINT_HUNT_THRESHOLD = 3;

function _isCmdHintDismissedRecently() {
  try {
    const raw = localStorage.getItem(CMDK_HINT_STORAGE_KEY);
    if (!raw) return false;
    // Back-compat: legacy "1" boolean — treat as "dismissed forever ago"
    // so the hint returns on the next session for existing users.
    if (raw === '1') return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < CMDK_HINT_REPEAT_MS;
  } catch {
    return false;
  }
}

function _markCmdHintDismissed() {
  try {
    localStorage.setItem(CMDK_HINT_STORAGE_KEY, String(Date.now()));
  } catch {
    // Ignore unavailable storage; the hint simply returns next session.
  }
}

export class Toolbar {
  constructor(container, iframe) {
    this._container = container;
    this._iframe = iframe;
    this._cmdHintDismissed = _isCmdHintDismissedRecently();
    this._menuHuntCount = 0;
    this._paletteOpenedThisSession = false;
    this._offs = [];
    this._render();
    this._listen();
  }

  _render() {
    this._container.classList.add('pb-toolbar');

    this._container.innerHTML = `
      <div id="pb-page-tabs-wrap" class="pb-page-tabs-wrap">
        <button type="button" class="pb-cmdk-hint" id="pb-cmdk-hint" data-tooltip="Open command palette" data-tooltip-shortcut="Ctrl+K · /">${CMDK_HINT_LABEL}</button>
      </div>
    `;

    const cmdHint = this._container.querySelector('#pb-cmdk-hint');
    if (cmdHint) {
      cmdHint.hidden = this._cmdHintDismissed;
      cmdHint.classList.toggle('pb-toolbar-pulse', !this._cmdHintDismissed);
    }
  }

  _listen() {
    const on = (name, fn) => this._offs.push(eventBus.on(name, fn));

    // ⌘K hint
    const cmdHint = this._container.querySelector('#pb-cmdk-hint');
    cmdHint?.addEventListener('click', () => {
      this._dismissCommandHint();
      eventBus.emit('toolbar:open-command-palette');
    });
    on('command-palette:opened', () => {
      this._paletteOpenedThisSession = true;
      this._dismissCommandHint();
    });
    // Every dropdown in the app announces itself here — the command bar's
    // project, share, publish, save and help menus included. The ⋮ that this
    // heuristic was written against is gone; the heuristic outlives it by one
    // phase because the hint it re-shows does.
    on('toolbar:menu-opened', () => this._noteMenuHunt());

    // History state changes. The undo/redo BUTTONS moved to the command bar's
    // row-2 left anchor (CMD-2) and own their own enabled/disabled chrome; what
    // stays here is the undo/redo TOAST, which is not a toolbar control.
    on('history-changed', ({ source, label }) => {
      if (source === 'undo' && label) showToast(`Undone: ${label}`, { duration: 2000 });
      else if (source === 'redo' && label) showToast(`Redone: ${label}`, { duration: 2000 });
    });
  }

  destroy() {
    for (const off of this._offs) if (typeof off === 'function') off();
    this._offs = [];
  }

  _dismissCommandHint() {
    this._menuHuntCount = 0;
    if (this._cmdHintDismissed) return;
    this._cmdHintDismissed = true;
    _markCmdHintDismissed();
    const hint = this._container.querySelector('#pb-cmdk-hint');
    if (hint) hint.hidden = true;
  }

  /** Menu-hunting heuristic — see CMDK_HINT_HUNT_THRESHOLD. */
  _noteMenuHunt() {
    if (this._paletteOpenedThisSession || !this._cmdHintDismissed) return;
    this._menuHuntCount += 1;
    if (this._menuHuntCount < CMDK_HINT_HUNT_THRESHOLD) return;
    this._cmdHintDismissed = false;
    const hint = this._container.querySelector('#pb-cmdk-hint');
    if (hint) {
      hint.hidden = false;
      hint.classList.add('pb-toolbar-pulse');
    }
  }
}
